<template>
  <dialog-layout
    title="Tournament points"
    :fullscreen="fullscreen"
    :navIcon="navIcon"
    :dark="dark"
    @pop-dialog="$emit('pop-dialog')"
  >
    <v-container>
      <div style="height: 400px">
        <LineChart v-if="chartData" ref="chartComponent" :data="chartData" :options="chartOptions" />
      </div>
    </v-container>
  </dialog-layout>
</template>

<script>
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  CategoryScale
} from "chart.js";
import { DialogLayout } from "vue-app-components";
import { generateShades, chartClickMixin, baseChartOptions } from "@/utils/chart-utils";
import { pointsEarnedInMatch } from "@/utils/tournament-points";

ChartJS.register(LinearScale, PointElement, LineElement, Title, Tooltip, Legend, CategoryScale);

export default {
  name: "dialog-tournament-chart",
  mixins: [chartClickMixin],
  components: {
    DialogLayout,
    LineChart: Line
  },
  props: {
    fullscreen: { type: Boolean, required: true },
    navIcon: { type: String, required: true },
    dark: { type: Boolean, default: false }
  },
  inject: ["$store"],
  data () {
    return {
      chartData: null,
      chartOptions: {
        ...baseChartOptions,
        scales: {
          x: {
            type: "category",
            title: {
              display: true,
              text: "Matches"
            }
          },
          y: {
            title: {
              display: true,
              text: "Points"
            },
            min: 0,
            stepSize: 1
          }
        },
        plugins: {
          ...baseChartOptions.plugins,
          tooltip: {
            ...baseChartOptions.plugins.tooltip,
            callbacks: {
              title: () => ""
            }
          }
        },
        onClick: (event, elements, chart) => {
          const nearest = chart.getElementsAtEventForMode(event, "nearest", { intersect: false }, false);
          this.handleChartClick(nearest);
        }
      }
    };
  },
  created () {
    this.$watch(() => this.$store.tournament?.inactiveMatches, () => {
      this.updateChartData();
    }, { immediate: true });
  },
  methods: {
    async updateChartData () {
      await this.loadInitialData();
      const tournament = this.$store.tournament;
      if (!tournament || !tournament.inactiveMatches) return;
      const sortedMatches = [...tournament.inactiveMatches]
        .sort((a, b) => a.createdAt.toMillis() - b.createdAt.toMillis());

      if (sortedMatches.length === 0) return;

      const labels = ["0", ...sortedMatches.map((_, i) => String(i + 1))];
      const playerPoints = {};
      const playerHistory = {};
      const currentUserId = this.$store.user.id;

      tournament.players.forEach(p => {
        playerHistory[p.id] = [0];
      });

      sortedMatches.forEach(match => {
        tournament.players.forEach(p => {
          if (!playerPoints[p.id]) playerPoints[p.id] = 0;
          playerPoints[p.id] += pointsEarnedInMatch(match, p.id);
        });
        tournament.players.forEach(p => {
          playerHistory[p.id].push(playerPoints[p.id] || 0);
        });
      });

      const datasets = [];
      const colors = this.$vuetify.theme.current.colors;
      const secondaryColor = colors.secondary;
      const primaryColor = colors.primary;
      const opponentShades = generateShades(secondaryColor, tournament.players.length);

      tournament.players.forEach((p, i) => {
        const data = playerHistory[p.id];
        if (!data || data.length === 0) return;
        const isCurrentUser = p.playerUserId === currentUserId;
        const color = isCurrentUser ? primaryColor : opponentShades[i];
        datasets.push({
          label: p.name || "Unknown",
          data,
          borderColor: color,
          backgroundColor: color,
          pointRadius: 3,
          tension: 0.1,
          borderWidth: isCurrentUser ? 2 : 1.5,
          _isCurrentUser: isCurrentUser
        });
      });

      this.chartData = { labels, datasets };
    },
    async loadInitialData () {
      const tournament = this.$store.tournament;
      if (!tournament) return;
      await tournament.promisePlayers;
      await tournament.promiseMatches;
      await Promise.all(tournament.matches.map(m => m.promiseTeams));
    },
    applySelectedStyles () {
      const chart = this.$refs.chartComponent?.chart;
      if (!chart) return;
      const datasets = chart.data.datasets;
      const selected = this.selectedDatasetIndex;
      datasets.forEach((ds, i) => {
        ds._originalColor ??= ds.borderColor;
        if (selected === null || i !== selected) {
          ds.borderWidth = ds._isCurrentUser ? 2 : 1.5;
          ds.borderColor = ds._originalColor;
          ds.backgroundColor = ds._originalColor;
          ds.pointRadius = 3;
        } else {
          ds.pointRadius = 4;
          if (ds._isCurrentUser) {
            ds.borderWidth = 3;
          } else {
            ds.borderWidth = 2;
            ds.borderColor = "#21356A";
          }
        }
      });
      chart.update();
    }
  }
};
</script>
